import {announce} from '@github-ui/aria-live'
import {debounce} from 'lodash-es'
import {useEffect, useMemo, useRef} from 'react'

import {isAutoAddWorkflow} from '../helpers/workflow-utilities'
import {type Cancellable, useSetTimeout} from '../hooks/common/timeouts/use-timeout'
import {useNavigate} from '../router'
import {useProjectRouteParams} from '../router/use-project-route-params'
import {PROJECT_WORKFLOW_CLIENT_ID_ROUTE} from '../routes'
import {useBulkAddNotifications} from '../state-providers/bulk-add-notifications/bulk-add-notifications-state-provider'
import {useWorkflows} from '../state-providers/workflows/use-workflows'
import {BulkAddResources} from '../strings'
import useToasts, {ToastType} from './toasts/use-toasts'

export const BulkAddNotifications = () => {
  const {bulkAddState, clearNotification} = useBulkAddNotifications()
  const {workflows} = useWorkflows()
  const projectRouteParams = useProjectRouteParams()
  const {addToast, addPersistedToast, updatePersistedToast, clearPersistedToast} = useToasts()
  const navigate = useNavigate()

  const {percentage, totalItemsAdded, totalItemsFailed, isComplete, initialImport, isQueryBased} = bulkAddState ?? {}

  const autoAddWorkflow = useMemo(() => workflows.find(isAutoAddWorkflow), [workflows])

  const autoAddWorkflowUrl = useMemo(() => {
    if (!autoAddWorkflow) return
    return PROJECT_WORKFLOW_CLIENT_ID_ROUTE.generatePath({
      ...projectRouteParams,
      workflowClientId: autoAddWorkflow.clientId,
    })
  }, [autoAddWorkflow, projectRouteParams])

  // We only want to consider the bulk add failed if no items were added at all
  const failed = totalItemsAdded === 0 && totalItemsFailed !== 0

  const hasPersistedToastRef = useRef(false)
  const lastMessageRef = useRef('')
  const completionToastTimerRef = useRef<Cancellable | null>(null)
  const persistedToastShownAtRef = useRef<number | null>(null)

  /**
   * Minimum time the in-progress toast is shown before transitioning to the success/failure toast.
   * This prevents the toast from flashing briefly for fast bulk adds.
   */
  const MIN_PERSISTED_TOAST_DISPLAY_MS = 300

  /**
   * Schedules the completion toast, ensuring the in-progress toast has been visible for at least
   * MIN_PERSISTED_TOAST_DISPLAY_MS before switching to the terminal state.
   */
  const scheduleCompletionToast = useSetTimeout(
    (type: ToastType, message: string, action?: {text: string; handleClick: () => void}) => {
      completionToastTimerRef.current = null
      if (hasPersistedToastRef.current) {
        clearPersistedToast()
        hasPersistedToastRef.current = false
      }
      persistedToastShownAtRef.current = null
      // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
      addToast({type, message, action})
      clearNotification()
    },
  )

  const debounceAnnouncement = useMemo(
    () =>
      debounce(
        (announcement: string, assertive: boolean = false) => {
          announce(announcement, {assertive})
        },
        200,
        {trailing: true, leading: false},
      ),
    [],
  )

  useEffect(() => {
    return () => debounceAnnouncement.cancel()
  }, [debounceAnnouncement])

  useEffect(() => {
    if (!bulkAddState) {
      completionToastTimerRef.current?.cancel()
      completionToastTimerRef.current = null
      if (hasPersistedToastRef.current) {
        clearPersistedToast()
      }
      hasPersistedToastRef.current = false
      persistedToastShownAtRef.current = null
      lastMessageRef.current = ''
      return
    }

    // Skip to avoid flashing "100% imported…" in progress message sent just before completion event
    if (percentage === 100 && !isComplete) return

    // No items matched the query (not a failure, just nothing to import)
    const noItems = isComplete && totalItemsAdded === 0 && totalItemsFailed === 0

    let message: string
    let toastType: ToastType

    if (isComplete && failed) {
      toastType = ToastType.error
      message = BulkAddResources.failedMessage(totalItemsFailed ?? 0)
    } else if (noItems) {
      toastType = ToastType.default
      message = BulkAddResources.noItemsMessage
    } else if (isComplete) {
      toastType = ToastType.success
      message = BulkAddResources.completionMessage(totalItemsFailed ?? 0, !!initialImport, !!isQueryBased)
    } else if (percentage !== null && percentage !== undefined && percentage > 0) {
      toastType = ToastType.default
      message = BulkAddResources.progressMessage(percentage)
    } else {
      toastType = ToastType.default
      message = BulkAddResources.startMessage
    }

    if (message === lastMessageRef.current) return

    lastMessageRef.current = message
    // Use assertive announcements for terminal states (success/error), polite for progress
    debounceAnnouncement(message, isComplete && !noItems)

    if (isComplete) {
      // Show workflow hint as a toast action for query-based successful imports
      const showWorkflowHint = isQueryBased && autoAddWorkflowUrl && !failed
      const action = showWorkflowHint
        ? {
            text: BulkAddResources.workflowButtonText,
            handleClick: () => void navigate(autoAddWorkflowUrl),
          }
        : undefined

      // Delay the success transition only when an in-progress toast was shown,
      // so it remains visible for at least MIN_PERSISTED_TOAST_DISPLAY_MS.
      if (!completionToastTimerRef.current) {
        const shownAt = persistedToastShownAtRef.current
        const hasShownPersistedToast = hasPersistedToastRef.current && shownAt !== null
        const delay = hasShownPersistedToast ? Math.max(MIN_PERSISTED_TOAST_DISPLAY_MS - (Date.now() - shownAt), 0) : 0
        completionToastTimerRef.current = scheduleCompletionToast(delay)(toastType, message, action)
      }
    } else {
      if (hasPersistedToastRef.current) {
        // Update the existing in-progress toast with the latest message/percentage
        updatePersistedToast({type: toastType, message})
      } else {
        // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
        addPersistedToast({type: toastType, message})
        hasPersistedToastRef.current = true
        persistedToastShownAtRef.current = Date.now()
      }
    }
  }, [
    debounceAnnouncement,
    bulkAddState,
    addPersistedToast,
    scheduleCompletionToast,
    updatePersistedToast,
    clearPersistedToast,
    clearNotification,
    navigate,
    isComplete,
    failed,
    totalItemsAdded,
    totalItemsFailed,
    percentage,
    initialImport,
    isQueryBased,
    autoAddWorkflowUrl,
  ])

  return null
}
